import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef } from "@mui/x-data-grid"
import { format } from "date-fns"

import Dialog from "@/components/dialog/Dialog"
import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import BasicButton from "@/components/button/BasicButton"
import { usePlayerCustomMetricHistoryQuery } from "@/hooks/api/useZoneQuery"
import { PlayerCustomMeticHistory } from "@/types/metric"
import { CellParamsType, ZoneType } from "@/app/settings/metric/zones/components/type"
import { DATE_TIME_FORMAT } from "@/constants/date"
import { createCustomHistoryDialogStore } from "@/app/settings/store/zone"

import "./customMetricHistoryDialog.scss"

export default function CustomMetricHistoryDialog({
  onClose,
  type,
  unit,
}: {
  onClose: () => void
  type: ZoneType
  unit: string
}) {
  const { t } = useTranslation()
  const [history, setHistory] = useState<PlayerCustomMeticHistory[]>([])
  const open = createCustomHistoryDialogStore((state) => state.isOpen)
  const idfPlayer = createCustomHistoryDialogStore((state) => state.idfPlayer)

  const newType = type === "acceleration" ? "accel" : type === "deceleration" ? "decel" : type
  const fixedNumber = type === "hr" ? 0 : 1
  const { data } = usePlayerCustomMetricHistoryQuery({ metric: newType, idfPlayer })

  useEffect(() => {
    if (data) {
      setHistory(data)
    }
  }, [data])

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: t("date"),
      width: 204,
      renderCell: (params: CellParamsType) => format(new Date(params.value), DATE_TIME_FORMAT),
    },
    ...Array.from({ length: 10 }, (_, i) => i + 1).map((i) => ({
      field: `${newType}${i}`,
      renderHeader: () => (
        <div>
          {t(`zone${i}`)}
          <br />
          <span className="zone-tab-table-header-unit">{unit}</span>
        </div>
      ),
      unit,
      width: 100,
      renderCell: (params: CellParamsType) => {
        const values = params.row.values as number[]

        if (i === 1) {
          return `0 ~ ${values[i - 1].toFixed(fixedNumber)}`
        }
        if (values[i - 1] && values[i - 2]) {
          return `${values[i - 2].toFixed(fixedNumber)} ~ ${values[i - 1].toFixed(fixedNumber)}`
        }
        if (i === values.length + 1 && values[i - 2]) {
          return `${values[i - 2].toFixed(fixedNumber)} ~ `
        }

        return ""
      },
    })),
  ]

  return (
    <Dialog
      title={t("customMetricHistory")}
      open={open}
      onClose={onClose}
      maxWidth="1260px"
      footer={
        <BasicButton type="default-outlined" size="md" onClick={onClose}>
          <p>{t("button.close")}</p>
        </BasicButton>
      }>
      <Table
        className="CustomMetricHistoryTable"
        columns={columns}
        rows={history}
        disableColumnResize
        slots={{
          pagination: Pagination,
        }}
        sx={{
          minHeight: history.length ? "" : "240px !important;",
        }}
        getRowId={(row) => row.date}
      />
    </Dialog>
  )
}
